import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Avatar,
  Chip,
  Autocomplete,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Switch,
  CircularProgress,
  Stack,
  OutlinedInput,
  InputAdornment,
  Tooltip,
  styled,
  Paper,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import { LoadingButton, TabContext, TabList, TabPanel } from "@mui/lab";
import { colorPalette } from "../utils";
import { getInitials } from "../utils";
import {
  useCreateProjectMutation,
  useCreateApplicationMutation,
  useGetConfigDetailsQuery,
  useGetDefaultStatusQuery,
  useGetListOfProjectsQuery,
  useGetListOfStatusQuery,
  useGetProjectForDropDownQuery,
  useGetProjectStatusLabelsMutation,
  useLazyGetJiraRequiredFieldsQuery,
  useUpdateProjectMutation,
} from "@/features/bughurd/api";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
import { useGetAzureBoardsProjectsQuery } from "@/features/jiraProject/api";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useTranslation } from "react-i18next";
import CustomColorPicker from "./CustomColorPicker";
import ApplicationSection from "./ApplicationSection";
import SlackIcon from "@/assets/icons/slack-1.svg?react";
import TeamsIcon from "@/assets/icons/microsoft-teams.svg?react";
import { useAppSelector } from "@/app/hooks";

const defaultFieldConfig = {
  epic: null,
  issueType: "TASK",
  isAutomaticCreateJira: false,
  isCreateComment: false,
  isSyncComment: false,
  syncFromJira: false,
  completedStatus: null,
  completedColumn: null,
};

const defaultAzureFieldConfig = {
  isAutomaticCreateAzure: false,
  isCreateComment: false,
  isSyncComment: false,
  syncFromAzure: false,
};

// ✅ Available skill set options
// const skillSetOptions = [
//   "Frontend Development",
//   "Backend Development",
//   "Full Stack Development",
//   "DevOps",
//   "QA/Testing",
//   "UI/UX Design",
//   "Mobile Development",
//   "Data Science",
//   "Machine Learning",
//   "Security",
// ];

// ✅ iOS-style Toggle Switch
const IOSSwitch = styled((props: any) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 51,
  height: 31,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(20px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: theme.palette.primary.main,
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 27,
    height: 27,
  },
  "& .MuiSwitch-track": {
    borderRadius: 31 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

export default function JiraConnectDrawer({
  open,
  onClose,
  projectDetails,
  platform = "JIRA",
}: {
  open: boolean;
  onClose: () => void;
  projectDetails?: any;
  platform?: string;
}) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation(["bugherd"]);
  const location = useLocation();
  const isIntegrationsPage = location.pathname.includes(
    "settings/integrations",
  );

  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(1);
  const [combinedResponse, setCombinedResponse] = useState([]);
  const [combinedStatus, setCombinedStatus] = useState([]);
  const [customRequiredFields, setCustomRequiredFields] = useState([]);
  const [projectListData, setProjectListData] = useState([]);
  const [isJiraConfigured, setIsJiraConfigured] = useState(false);
  const [isAzureConfigured, setIsAzureConfigured] = useState(false);
  const [isKeyAvailable, setIsKeyAvailable] = useState(false);
  const [azureTabValue, setAzureTabValue] = useState(1);

  const currentUserId =
    JSON.parse(localStorage.getItem("currentUser") || "{}")?._id || "";

  const baseUrl = window?.location?.origin;
  const isSniffer = baseUrl?.includes("sniffer") || false;
  const isPmoHub = baseUrl?.includes("pmohub") || false;

  const [projectInfo, setProjectInfo] = useState({
    name: "",
    projectKey: "",
    domain: "",
    owner: currentUserId,
    platform: "",
    boardType: "KANBAN",
    projectType: "ISSUE",
    configurationType: "SUPPORT",
    icon: "#4DABF7",
    jiraConfig: [],
    azureBoardsConfig: [],
    users: [],
    skillSet: [],
    isEstimationRequired: false,
    communicationChannel: "NONE",
    communicationChannelData: null,
    preference: { isFeedback: true, isBoard: true, isLiveLog: true },
    applications: [],
  });

  const handleColorChange = (color: string) => {
    setProjectInfo({
      ...projectInfo,
      icon: color,
    });
  };

  const handleChange = (
    key: string,
    fieldName?: string,
    fieldKey?: string,
    value?: string | boolean,
    dataType?: string,
    allowedValues?: any,
  ) => {
    const processedValue = dataType === "number" ? Number(value) : value;

    setProjectInfo((prev) => {
      const updatedJiraConfig = prev.jiraConfig.map((config) => {
        if (config.key === key) {
          return {
            ...config,
            defaultFields: {
              ...config?.defaultFields,
              [fieldName]: {
                ...config?.defaultFields?.[fieldName],
                [fieldKey]: processedValue,
                method: Boolean(value),
                type: dataType,
                ...(allowedValues && {
                  allowedValues,
                }),
              },
            },
          };
        }
        return config;
      });

      return { ...prev, jiraConfig: updatedJiraConfig };
    });
  };

  const { data: defaultStatus } = useGetDefaultStatusQuery({});
  const { data: statusList } = useGetListOfStatusQuery(
    { projectId: id },
    { skip: !id },
  );

  const updateFieldConfig = (
    key: string,
    fieldKey: string,
    value?: string | boolean,
    dataType?: string,
  ) => {
    const processedValue = dataType === "boolean" ? Boolean(value) : value;

    setProjectInfo((prev) => {
      const updatedJiraConfig = prev.jiraConfig.map((config) => {
        if (config.key === key) {
          const updatedConfig = {
            ...config,
            fieldConfig: {
              ...config.fieldConfig,
              [fieldKey]: processedValue,
            },
          };
          if (fieldKey === "isAutomaticCreateJira") {
            updatedConfig.syncToJira = processedValue;
          } else if (fieldKey === "isCreateComment") {
            updatedConfig.syncCommentToJira = processedValue;
          } else if (fieldKey === "isSyncComment") {
            updatedConfig.syncCommentFromJira = processedValue;
          } else if (fieldKey === "syncFromJira") {
            updatedConfig.syncFromJira = processedValue;
          }

          return updatedConfig;
        }
        return config;
      });

      return { ...prev, jiraConfig: updatedJiraConfig };
    });
  };

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (projectDetails) {
      // Transform from new integration format to UI state format
      let jiraConfig = projectDetails?.jiraConfig || [];
      let azureBoardsConfig = projectDetails?.azureBoardsConfig || [];

      // Handle new integrationConfig format
      if (
        projectDetails?.integrationConfig &&
        projectDetails?.integrationType
      ) {
        const config = projectDetails.integrationConfig;

        if (projectDetails.integrationType === "JIRA") {
          jiraConfig = [
            {
              key: config.projectKey,
              projectId: config.projectId,
              projectData: config.projectData || {},
              defaultFields: config.defaultFields || {},
              fieldConfig: config.fieldConfig || {},
              syncFromJira: config.syncFrom || false,
              syncToJira: config.syncTo || false,
              syncCommentFromJira: config.syncCommentFrom || false,
              syncCommentToJira: config.syncCommentTo || false,
            },
          ];
        } else if (projectDetails.integrationType === "AZURE") {
          azureBoardsConfig = [
            {
              projectId: config.projectId,
              projectKey: config.projectKey || "",
              projectData: config.projectData || {},
              defaultFields: config.defaultFields || {},
              fieldConfig: config.fieldConfig || {},
              organization: config.organization || "",
              projectName: config.projectName || "",
              syncFromAzure: config.syncFrom || false,
              syncToAzure: config.syncTo || false,
              syncCommentFromAzure: config.syncCommentFrom || false,
              syncCommentToAzure: config.syncCommentTo || false,
            },
          ];
        }
      }

      const communicationChannel =
        projectDetails?.communicationChannel || "NONE";

      // Handle communicationChannelData - ensure webhookUrl is a string, not array
      let communicationChannelData = null;
      if (
        communicationChannel !== "NONE" &&
        projectDetails?.communicationChannelData
      ) {
        const webhookUrl = projectDetails.communicationChannelData.webhookUrl;
        communicationChannelData = {
          webhookUrl: Array.isArray(webhookUrl) ? webhookUrl[0] : webhookUrl,
        };
      }

      setProjectInfo((_prev) => ({
        name: projectDetails.name,
        projectKey: projectDetails?.projectKey,
        owner: projectDetails?.owner,
        platform: platform || projectDetails?.platform,
        boardType: projectDetails?.boardType || "KANBAN",
        projectType: projectDetails?.projectType || "ISSUE",
        configurationType: projectDetails?.configurationType || "SUPPORT",
        icon: projectDetails?.icon || "#4DABF7",
        jiraConfig,
        azureBoardsConfig,
        users: projectDetails?.users || [],
        skillSet: projectDetails?.skillSet || [],
        isEstimationRequired: projectDetails?.isEstimationRequired || false,
        preference: projectDetails?.preference || {
          isFeedback: true,
          isBoard: true,
          isLiveLog: true,
        },
        domain: projectDetails?.domain,
        communicationChannel,
        communicationChannelData,
        applications: projectDetails?.applications || [],
      }));
      setIsJiraConfigured(platform === "JIRA" ? true : false);
      setIsAzureConfigured(platform === "AZURE" ? true : false);
    } else {
      // For new projects, set platform from prop
      setProjectInfo((prev) => ({
        ...prev,
        platform: platform,
      }));
      setIsJiraConfigured(platform === "JIRA" ? true : false);
      setIsAzureConfigured(platform === "AZURE" ? true : false);
    }
  }, [projectDetails, platform]);

  const { data: jiraProjectsData, isLoading: isLoadingProjects } =
    useGetProjectForDropDownQuery({});
  const jiraProjects = jiraProjectsData?.data?.decryptedData?.data || [];
  const { data: configDataList, isLoading: isLoadingConfig } =
    useGetConfigDetailsQuery({});
  const configData = configDataList?.data?.decryptedData;

  const [getRequiredFields] = useLazyGetJiraRequiredFieldsQuery();
  const [getProjectStatusLabels] = useGetProjectStatusLabelsMutation();
  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();
  const [updateProject, { isLoading: isUpdating }] = useUpdateProjectMutation();
  const [createApplication] = useCreateApplicationMutation();
  const { data: companyMemberData } = useGetCompanyMembersQuery({});
  // const companyId = JSON.parse(localStorage.getItem("currentUser") || "{}")?.companyId;
  const { companyDetails } = useAppSelector((state) => state.appGlobal);

  const { data: azureProjectsData, isLoading: isLoadingAzureProjects } =
    useGetAzureBoardsProjectsQuery(
      { companyId: companyDetails?.companyId },
      { skip: platform !== "AZURE" },
    );
  const azureProjects =
    azureProjectsData?.data?.decryptedData?.value ||
    azureProjectsData?.data?.decryptedData ||
    [];
  const {
    data: projectList,
    isLoading: isProjectListLoading,
    isFetching: isProjectListFetching,
    isSuccess: isProjectListSuccess,
  } = useGetListOfProjectsQuery(
    { filterBy: "projectKey", filterValue: projectInfo?.projectKey },
    { skip: !projectInfo?.projectKey, refetchOnMountOrArgChange: true },
  );

  const prevKeysRef = useRef<string[]>([]);
  const prevKeysStatusRef = useRef<string[]>([]);

  useEffect(() => {
    const keys = projectInfo.jiraConfig.map((config) => config.key);
    const prevKeys = prevKeysRef.current;

    const newOrRemovedKeys = keys?.filter((key) => !prevKeys.includes(key));

    setCombinedResponse((prev) =>
      prev.filter((item) => keys.includes(item.key)),
    );

    if (newOrRemovedKeys.length === 0) {
      prevKeysRef.current = keys;
      return;
    }

    const fetchData = async () => {
      if (keys.length === 0) return setCombinedResponse([]);
      setLoading(true);
      const allData = [];
      for (const key of newOrRemovedKeys) {
        const name =
          jiraProjects?.length > 0
            ? jiraProjects?.find((project) => project.key === key)?.name
            : "";
        const result = await getRequiredFields(key).unwrap();
        if (
          result &&
          result.length > 0 &&
          result?.status !== "NO_RECORD_FOUND"
        ) {
          const filteredResult = await result?.filter(
            (item) =>
              item?.required === true &&
              item?.fieldKey !== "summary" &&
              item?.fieldKey !== "description" &&
              item?.fieldKey !== "assignees" &&
              item?.fieldKey !== "reporter" &&
              item?.fieldKey !== "priority" &&
              item?.fieldKey !== "status" &&
              item?.fieldKey !== "issuetype" &&
              item?.fieldKey !== "project" &&
              item?.fieldKey !== "projectKey" &&
              item?.fieldKey !== "type",
          );
          const issueTypes = await result?.filter(
            (item) => item?.fieldKey === "issuetype",
          )?.allowedValues;

          if (!customRequiredFields.some((v) => v.key === key)) {
            setCustomRequiredFields([
              ...customRequiredFields,
              { key, name, data: filteredResult },
            ]);
          }

          await allData.push({
            key,
            name,
            data: filteredResult,
            issueTypes: issueTypes,
          });
        }
      }

      setCombinedResponse((prev) => {
        const updatedData = prev?.filter((item) => keys.includes(item.key));

        return [
          ...updatedData,
          ...allData?.filter(
            (newItem) => !prev.some((item) => item.key === newItem.key),
          ),
        ];
      });

      setLoading(false);
    };

    fetchData();
    prevKeysRef.current = keys;
  }, [projectInfo.jiraConfig]);

  useEffect(() => {
    const keys = projectInfo.jiraConfig.map((config) => config.key);
    const prevKeys = prevKeysStatusRef.current;

    const newOrRemovedKeys = keys?.filter((key) => !prevKeys.includes(key));

    setCombinedStatus((prev) =>
      prev?.filter((item) => keys.includes(item.key)),
    );

    if (newOrRemovedKeys.length === 0) {
      prevKeysStatusRef.current = keys;
      return;
    }

    const fetchStatusLabels = async () => {
      if (keys.length === 0) return setCombinedStatus([]);

      setLoading(true);
      const allData = [];
      for (const key of newOrRemovedKeys) {
        const name =
          jiraProjects?.length > 0
            ? jiraProjects?.find((project) => project.key === key)?.name
            : "";
        await getProjectStatusLabels({ keys: [key] })
          .unwrap()
          .then((res) => {
            if (res && res?.statuses && res.statuses?.length > 0) {
              allData.push({ key, name, status: res?.statuses });
            }
          })
          .catch((error) => console.error("rejected", error));
      }

      setCombinedStatus((prev) => {
        const updatedStatus = prev?.filter((item) => keys.includes(item.key));

        return [
          ...updatedStatus,
          ...allData?.filter(
            (newItem) => !prev.some((item) => item.key === newItem.key),
          ),
        ];
      });

      setLoading(false);
    };

    fetchStatusLabels();
    prevKeysStatusRef.current = keys;
  }, [projectInfo.jiraConfig]);

  useEffect(() => {
    if (isProjectListSuccess) {
      let projectListRes = projectList?.data?.decryptedData?.items;
      if (id) {
        projectListRes = projectList?.data?.decryptedData?.items?.filter(
          (v) => v._id !== id,
        );
      }
      setProjectListData(projectListRes);
      setIsKeyAvailable(projectListRes?.length === 0);
    }
  }, [isProjectListSuccess, projectList]);

  const getDefaultValue = (fieldType) =>
    ({
      string: "",
      number: 0,
      array: [],
      boolean: false,
    })[fieldType] ?? null;

  const generateKey = (str) =>
    str
      .match(/\b\w/g)
      ?.map((char) => char.toUpperCase())
      .join("") || "";

  const updateDefaultFields = () => {
    return projectInfo?.jiraConfig?.map((config) => {
      const matchingCustomField = customRequiredFields.find(
        (field) => field.key === config.key,
      );
      if (matchingCustomField && matchingCustomField.data.length > 0) {
        const updatedDefaultFields = { ...config.defaultFields };
        matchingCustomField.data.forEach((field) => {
          if (!updatedDefaultFields[field.fieldName]) {
            updatedDefaultFields[field.fieldName] = {
              [field.fieldKey]: getDefaultValue(field?.dataType),
              method: false,
              type: field?.dataType,
              ...(field?.allowedValues && {
                allowedValues: field.allowedValues,
              }),
            };
          }
        });
        return { ...config, defaultFields: updatedDefaultFields };
      }
      return config;
    });
  };

  const resetState = () => {
    setProjectInfo({
      name: "",
      projectKey: "",
      owner: currentUserId,
      platform: "",
      boardType: "KANBAN",
      projectType: "ISSUE",
      configurationType: "SUPPORT",
      icon: "#4DABF7",
      jiraConfig: [],
      azureBoardsConfig: [],
      users: [],
      skillSet: [],
      isEstimationRequired: false,
      preference: { isFeedback: true, isBoard: true, isLiveLog: true },
      domain: "",
      communicationChannel: "NONE",
      communicationChannelData: null,
      applications: [],
    });
    setTabValue(1);
    setAzureTabValue(1);
    setCombinedResponse([]);
    setCombinedStatus([]);
    setCustomRequiredFields([]);
    setIsJiraConfigured(false);
    setIsAzureConfigured(false);
  };

  const handleCancel = () => {
    if (!id) {
      resetState();
    }
    onClose();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Transform the payload to match new integration pattern
      const transformPayload = (info) => {
        const { jiraConfig, azureBoardsConfig, platform, ...rest } = info;

        // For communication channels (SLACK/TEAMS), preserve existing integration data
        let integrationType = projectDetails?.integrationType || "NONE";
        let integrationConfig = projectDetails?.integrationConfig || null;

        // Only update integration type and config if platform is a project management tool
        if (platform === "JIRA" && jiraConfig?.length > 0) {
          integrationType = "JIRA";
          const config = jiraConfig[0];
          integrationConfig = {
            projectId: config.projectId,
            projectKey: config.key,
            projectData: config.projectData || {},
            fieldConfig: config.fieldConfig || {},
            defaultFields: config.defaultFields || {},
            projectName: config.projectData?.name || "",
            syncFrom: config.syncFromJira || false,
            syncTo: config.syncToJira || false,
            syncCommentFrom: config.syncCommentFromJira || false,
            syncCommentTo: config.syncCommentToJira || false,
          };
        } else if (platform === "AZURE" && azureBoardsConfig?.length > 0) {
          integrationType = "AZURE";
          const config = azureBoardsConfig[0];
          integrationConfig = {
            projectId: config.projectId,
            projectKey: config.projectKey || "",
            projectData: config.projectData || {},
            fieldConfig: config.fieldConfig || {},
            defaultFields: config.defaultFields || {},
            organization: config.organization || "",
            projectName: config.projectName || config.projectData?.name || "",
            syncFrom: config.syncFromAzure || false,
            syncTo: config.syncToAzure || false,
            syncCommentFrom: config.syncCommentFromAzure || false,
            syncCommentTo: config.syncCommentToAzure || false,
          };
        } else if (platform === "ASANA") {
          integrationType = "ASANA";
          // Add Asana-specific config when implemented
        }

        // Communication channel - get from projectInfo or default to NONE
        const communicationChannel = rest.communicationChannel || "NONE";
        const communicationChannelData =
          communicationChannel === "NONE"
            ? null
            : rest.communicationChannelData || { webhookUrl: "" };

        return {
          ...rest,
          platform: projectDetails?.platform || platform || "NONE",
          integrationType,
          integrationConfig,
          communicationChannel,
          communicationChannelData,
          // Keep legacy fields for backward compatibility if needed
          jiraConfig,
          azureBoardsConfig,
        };
      };

      if (id) {
        const transformedPayload = transformPayload(projectInfo);
        const payload = { projectId: id, ...transformedPayload };
        await updateProject(payload).then((res) => {
          if (res?.data) {
            onClose();
            toast.success(t("Project updated successfully"));
          } else {
            toast.error(t("Failed to update project"));
          }
        });
      } else {
        const updatedInfo = {
          ...projectInfo,
          jiraConfig: updateDefaultFields(),
        };
        const transformedPayload = transformPayload(updatedInfo);
        await createProject(transformedPayload).then(async (res) => {
          if (res?.data) {
            console.log("Project created with response:", res);
            const projectId = res.data?.data?.decryptedData?._id;

            // Create applications if any
            if (projectInfo.applications?.length > 0) {
              try {
                await Promise.all(
                  projectInfo.applications.map((app: any) => {
                    const payload: any = {
                      name: app.name,
                      applicationType: app.type?.toUpperCase(),
                      description: app.description || "",
                      repositories: app.repos
                        ?.filter((r: any) => r.url)
                        .map((r: any) => ({
                          repositoryUrl: r.url,
                          defaultBranch: r.branch || "main",
                          description: r.description || "",
                          currentReleaseTag: r.currentRelease || "",
                          incomingReleaseTag: r.incomingRelease || "",
                        })),
                    };

                    // Add saasPlatformBinding for SAAS and HYBRID types
                    if (app.type === "saas" || app.type === "hybrid") {
                      payload.saasPlatformBinding = {
                        platform: app.platform || "",
                        currentRelease: app.platformVersion || "",
                        environmentUrl: app.envUrl || "",
                        orgInstanceId: app.orgId || "",
                      };
                    }

                    return createApplication({ projectId, ...payload });
                  }),
                );
              } catch (err) {
                console.error("Failed to create some applications:", err);
              }
            }

            resetState();
            onClose();
            toast.success(t("Project created successfully"));
            setIsKeyAvailable(false);
          } else {
            toast.error(t("Failed to create project"));
          }
        });
      }
    } catch (error) {
      toast.error(t("Operation failed"));
    }
  };

  const handleClickHere = () => {
    onClose();
    navigate("/company-settings/settings/integration");
  };

  const handleProjectChange = (_, newValue) => {
    setProjectInfo((prev) => {
      if (!newValue) {
        return {
          ...prev,
          jiraConfig: [],
        };
      }

      const prevProject = prev.jiraConfig.find((p) => p.key === newValue.key);

      const updatedJiraConfig = [
        {
          key: newValue.key,
          projectId: newValue.id,
          projectData: newValue,
          defaultFields: prevProject ? prevProject.defaultFields : {},
          fieldConfig: prevProject
            ? prevProject.fieldConfig
            : { ...defaultFieldConfig },
          syncToJira: prevProject ? prevProject.syncToJira : false,
          syncCommentToJira: prevProject
            ? prevProject.syncCommentToJira
            : false,
          syncCommentFromJira: prevProject
            ? prevProject.syncCommentFromJira
            : false,
          syncFromJira: prevProject ? prevProject.syncFromJira : false,
        },
      ];

      return {
        ...prev,
        jiraConfig: updatedJiraConfig,
      };
    });
  };

  const handleAzureProjectChange = (_, newValue) => {
    setProjectInfo((prev) => {
      if (!newValue) {
        return {
          ...prev,
          azureBoardsConfig: [],
        };
      }

      const prevConfig = prev.azureBoardsConfig.find(
        (p) => p.projectId === newValue.id,
      );

      const existingOrg = prev.azureBoardsConfig?.[0]?.organization || "";

      const updatedAzureConfig = [
        {
          organization: prevConfig ? prevConfig.organization : existingOrg,
          projectName: newValue.name,
          projectId: newValue.id,
          projectData: newValue,
          fieldConfig: prevConfig
            ? prevConfig.fieldConfig
            : { ...defaultAzureFieldConfig },
          syncToAzure: prevConfig ? prevConfig.syncToAzure : false,
          syncCommentToAzure: prevConfig
            ? prevConfig.syncCommentToAzure
            : false,
          syncCommentFromAzure: prevConfig
            ? prevConfig.syncCommentFromAzure
            : false,
          syncFromAzure: prevConfig ? prevConfig.syncFromAzure : false,
          defaultFields: prevConfig ? prevConfig.defaultFields : {},
        },
      ];

      return {
        ...prev,
        azureBoardsConfig: updatedAzureConfig,
      };
    });
  };

  const updateAzureFieldConfig = (
    projectId: string,
    fieldKey: string,
    value?: string | boolean,
    dataType?: string,
  ) => {
    const processedValue = dataType === "boolean" ? Boolean(value) : value;

    setProjectInfo((prev) => {
      const updatedAzureConfig = prev.azureBoardsConfig.map((config) => {
        if (config.projectId === projectId) {
          const updatedConfig = {
            ...config,
            fieldConfig: {
              ...config.fieldConfig,
              [fieldKey]: processedValue,
            },
          };
          if (fieldKey === "isAutomaticCreateAzure") {
            updatedConfig.syncToAzure = processedValue;
          } else if (fieldKey === "isCreateComment") {
            updatedConfig.syncCommentToAzure = processedValue;
          } else if (fieldKey === "isSyncComment") {
            updatedConfig.syncCommentFromAzure = processedValue;
          } else if (fieldKey === "syncFromAzure") {
            updatedConfig.syncFromAzure = processedValue;
          }

          return updatedConfig;
        }
        return config;
      });

      return { ...prev, azureBoardsConfig: updatedAzureConfig };
    });
  };

  useEffect(() => {
    if (Array.isArray(configData) && configData?.length > 0) {
      const findJiraConfigExist = configData.find(
        (config) => config.type === "JIRA",
      );
      setIsJiraConfigured(!!findJiraConfigExist);

      const findAzureConfigExist = configData.find(
        (config) => config.type === "AZURE",
      );
      setIsAzureConfigured(!!findAzureConfigExist);
    } else {
      setIsJiraConfigured(false);
      setIsAzureConfigured(false);
    }
  }, [configData, open]);

  const isLoadingProjectList = isProjectListLoading || isProjectListFetching;

  const getUserAvatarColor = (index: number) => {
    return colorPalette[index % colorPalette.length];
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <form onSubmit={handleSave} style={{ height: "100%" }}>
        <Box
          sx={{
            width: "90vw",
            minWidth: 400,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.background.default
                : "#f8f9fa",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: (theme) => theme.palette.background.paper,
              flexShrink: 0,
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  background: (theme) => `${theme.palette.primary.main}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckIcon
                  sx={{
                    fontSize: 20,
                    color: (theme) => theme.palette.primary.main,
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  fontSize="15px"
                >
                  {id ? t("Configuration") : t("Add New Project")}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="11.5px"
                >
                  {id
                    ? platform === "AZURE"
                      ? t("Azure Boards Integration")
                      : platform === "JIRA"
                        ? t("Jira Integration")
                        : platform === "ASANA"
                          ? t("Asana Integration")
                          : platform === "SLACK"
                            ? t("Slack Configuration")
                            : platform === "TEAMS"
                              ? t("Microsoft Teams Configuration")
                              : t("Integration Configuration")
                    : t("Set up your project details")}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                sx={{
                  minWidth: "90px",
                  borderRadius: "7px",
                  textTransform: "none",
                  fontWeight: 500,
                }}
                onClick={handleCancel}
              >
                {t("Cancel")}
              </Button>
              <LoadingButton
                variant="contained"
                type="submit"
                disabled={
                  !isKeyAvailable ||
                  !projectInfo.domain ||
                  (!projectInfo.preference.isFeedback &&
                    !projectInfo.preference.isBoard &&
                    !projectInfo.preference.isLiveLog)
                }
                loading={isCreating || isUpdating}
                sx={{
                  minWidth: "90px",
                  borderRadius: "7px",
                  textTransform: "none",
                  fontWeight: 500,
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                {t("Save")}
              </LoadingButton>
            </Box>
          </Box>
          {!isLoadingConfig && !isLoadingProjects ? (
            <Box
              sx={{
                p: 3,
                flex: 1,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.2)"
                      : "rgba(0,0,0,0.15)",
                  borderRadius: "3px",
                },
                "& .MuiFormLabel-asterisk": {
                  color: "error.main",
                },
              }}
            >
              <Box display="flex" gap={2.5} mb={2.5}>
                {/* Project Details Section */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: (theme) => theme.palette.background.paper,
                    flex: 1,
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 1px 8px rgba(0,0,0,0.2)"
                        : "0 1px 8px rgba(0,0,0,0.04)",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1.2}
                    mb={2}
                    pb={1.5}
                    sx={{
                      borderBottom: (theme) =>
                        `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: (theme) =>
                          `${theme.palette.primary.main}1A`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AssignmentOutlinedIcon
                        sx={{
                          fontSize: 17,
                          color: (theme) => theme.palette.primary.main,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      fontSize="13.5px"
                    >
                      {t("Project Details")}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <TextField
                      label={t("Project Name")}
                      fullWidth
                      variant="outlined"
                      value={projectInfo.name}
                      required
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setProjectInfo({
                          ...projectInfo,
                          name: e.target.value,
                          projectKey: generateKey(e.target.value),
                        })
                      }
                      disabled={id ? true : false}
                    />
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel htmlFor="snr-projectKey" required>
                        {t("Project Key")}
                      </InputLabel>
                      <OutlinedInput
                        id="snr-projectKey"
                        value={projectInfo.projectKey}
                        required
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setProjectInfo({
                            ...projectInfo,
                            projectKey: e.target.value,
                          })
                        }
                        disabled={id ? true : false}
                        endAdornment={
                          <InputAdornment position="end">
                            {isLoadingProjectList && (
                              <CircularProgress size="18px" />
                            )}
                            {!isLoadingProjectList &&
                              projectListData?.length > 0 && (
                                <Tooltip
                                  title={t("Project key is not available")}
                                  arrow
                                >
                                  <CloseIcon
                                    sx={{ color: "#bb2124", fontSize: 18 }}
                                  />
                                </Tooltip>
                              )}
                            {!isLoadingProjectList && isKeyAvailable && (
                              <Tooltip
                                title={t("Project key is available")}
                                arrow
                              >
                                <CheckIcon
                                  sx={{ color: "#22bb33", fontSize: 18 }}
                                />
                              </Tooltip>
                            )}
                          </InputAdornment>
                        }
                        label={t("Project Key")}
                      />
                    </FormControl>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <TextField
                      label={t("Connect Website Domain")}
                      fullWidth
                      type="url"
                      variant="outlined"
                      value={projectInfo.domain}
                      required
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setProjectInfo({
                          ...projectInfo,
                          domain: e.target.value,
                        })
                      }
                      disabled={id ? true : false}
                    />
                  </Box>
                  {!isIntegrationsPage && !isPmoHub && (
                    <Box display="flex" gap={2}>
                      <Autocomplete
                        multiple
                        options={companyMemberData?.data?.decryptedData || []}
                        getOptionLabel={(option) => option?.fullName || ""}
                        disableCloseOnSelect
                        value={
                          projectInfo?.users
                            ?.map((userId) =>
                              companyMemberData?.data?.decryptedData?.find(
                                (member) => member._id === userId,
                              ),
                            )
                            .filter(Boolean) || []
                        }
                        onChange={(_, newValue) => {
                          setProjectInfo({
                            ...projectInfo,
                            users: newValue.map((user) => user._id),
                          });
                        }}
                        renderTags={(tagValues, getTagProps) =>
                          tagValues?.map((option, index) => {
                            if (!option?._id || !option?.fullName) {
                              console.error("Invalid user data", option);
                              return null;
                            }
                            return (
                              <Chip
                                key={option?._id || index}
                                label={option?.fullName}
                                avatar={
                                  <Avatar
                                    sx={{
                                      bgcolor: `${getUserAvatarColor(index)} !important`,
                                      width: 24,
                                      height: 24,
                                      fontSize: "0.75rem",
                                      color: "#fff !important",
                                    }}
                                  >
                                    {getInitials(option?.fullName)}
                                  </Avatar>
                                }
                                {...getTagProps({ index })}
                                onDelete={() => {
                                  const updatedUsers = projectInfo?.users?.filter(
                                    (userId) => userId !== option._id,
                                  );
                                  setProjectInfo({
                                    ...projectInfo,
                                    users: updatedUsers,
                                  });
                                }}
                              />
                            );
                          })
                        }
                        renderOption={(props, option, { selected }) => {
                          const optionIndex =
                            companyMemberData?.data?.decryptedData?.findIndex(
                              (item) => item._id === option._id,
                            ) || 0;
                          return (
                            <li {...props}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <Avatar
                                  sx={{
                                    backgroundColor:
                                      getUserAvatarColor(optionIndex),
                                    width: 32,
                                    height: 32,
                                    fontSize: "0.75rem",
                                    color: "#fff",
                                    mr: 1.5,
                                  }}
                                >
                                  {getInitials(option?.fullName)}
                                </Avatar>
                                <Typography sx={{ flexGrow: 1 }}>
                                  {option?.fullName}
                                </Typography>
                                {selected && (
                                  <Check
                                    sx={{
                                      color: "primary.main",
                                      ml: 1,
                                    }}
                                  />
                                )}
                              </Box>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("Add Users")}
                            placeholder={t("Select users to add to project")}
                            fullWidth
                            required={projectInfo?.users?.length === 0}
                          />
                        )}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  )}
                </Paper>

                {/* Configuration Section */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: (theme) => theme.palette.background.paper,
                    flex: 1,
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 1px 8px rgba(0,0,0,0.2)"
                        : "0 1px 8px rgba(0,0,0,0.04)",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1.2}
                    mb={2}
                    pb={1.5}
                    sx={{
                      borderBottom: (theme) =>
                        `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: (theme) =>
                          `${theme.palette.primary.main}1A`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TuneIcon
                        sx={{
                          fontSize: 17,
                          color: (theme) => theme.palette.primary.main,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      fontSize="13.5px"
                    >
                      {t("Configuration")}
                    </Typography>
                  </Box>
                  <FormControl fullWidth required sx={{ mb: 2 }}>
                    <InputLabel id="project-type-label">
                      {t("Configuration Type")}
                    </InputLabel>
                    <Select
                      labelId="project-type-label"
                      value={projectInfo?.configurationType}
                      onChange={(e) =>
                        setProjectInfo({
                          ...projectInfo,
                          configurationType: e.target.value,
                          preference:
                            e.target.value === "IT"
                              ? { isFeedback: false, isBoard: true, isLiveLog: false }
                              : projectInfo.preference,
                        })
                      }
                      name="configurationType"
                      label={t("Configuration Type")}
                    >
                      <MenuItem value="SUPPORT">{t("Support")}</MenuItem>
                      <MenuItem value="IT">{t("IT")}</MenuItem>
                    </Select>
                  </FormControl>
                  <Autocomplete
                    multiple
                    disabled={projectInfo.configurationType === "IT"}
                    options={
                      projectInfo.configurationType === "IT"
                        ? ["Task Board"]
                        : ["Feedback", "Task Board", "Live Logs"]
                    }
                    value={
                      [
                        projectInfo.preference.isFeedback && "Feedback",
                        projectInfo.preference.isBoard && "Task Board",
                        projectInfo.preference.isLiveLog && "Live Logs",
                      ].filter(Boolean) as string[]
                    }
                    onChange={(_, newValue) => {
                      setProjectInfo({
                        ...projectInfo,
                        preference: {
                          isFeedback: newValue.includes("Feedback"),
                          isBoard: newValue.includes("Task Board"),
                          isLiveLog: newValue.includes("Live Logs"),
                        },
                      });
                    }}
                    renderTags={(tagValues, getTagProps) =>
                      tagValues.map((option, index) => (
                        <Chip
                          key={index}
                          label={t(option)}
                          {...getTagProps({ index })}
                          color="primary"
                          variant="outlined"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("Project Preference")}
                        placeholder={t("Select preferences")}
                        fullWidth
                        required={
                          !projectInfo.preference.isFeedback &&
                          !projectInfo.preference.isBoard &&
                          !projectInfo.preference.isLiveLog
                        }
                      />
                    )}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <Box display="flex" gap={2} alignItems="center">
                    <Box
                      width="100%"
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        borderRadius: "8px",
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <CustomColorPicker
                        value={projectInfo.icon}
                        onChange={handleColorChange}
                        label={t("Project Color")}
                        size="medium"
                        variant="outlined"
                        showHexValue={true}
                      />
                    </Box>
                    {(!isSniffer || isPmoHub) && (
                      <FormControl fullWidth required>
                        <InputLabel id="board-type-label">
                          {t("Select Board Type")}
                        </InputLabel>
                        <Select
                          labelId="board-type-label"
                          value={projectInfo?.boardType}
                          onChange={(e) =>
                            setProjectInfo({
                              ...projectInfo,
                              boardType: e.target.value,
                            })
                          }
                          name="boardType"
                          label={t("Select Board Type")}
                        >
                          <MenuItem value="KANBAN">
                            {t("Kanban Board")}
                          </MenuItem>
                          <MenuItem value="SCRUM">{t("Scrum Board")}</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Paper>
              </Box>

              {/* Team & Skills Section */}
              {isPmoHub &&<Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 2.5,
                  borderRadius: "12px",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  bgcolor: (theme) => theme.palette.background.paper,
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 1px 8px rgba(0,0,0,0.2)"
                      : "0 1px 8px rgba(0,0,0,0.04)",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1.2}
                  mb={2}
                  pb={1.5}
                  sx={{
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: (theme) => `${theme.palette.primary.main}1A`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <GroupsOutlinedIcon
                      sx={{
                        fontSize: 17,
                        color: (theme) => theme.palette.primary.main,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    fontSize="13.5px"
                  >
                    {t("Team & Skills")}
                  </Typography>
                </Box>
                {!isIntegrationsPage && (
                  <Box display="flex" gap={2} mb={2}>
                  <Autocomplete
                      multiple
                      options={companyMemberData?.data?.decryptedData || []}
                      getOptionLabel={(option) => option?.fullName || ""}
                      disableCloseOnSelect
                      value={
                        projectInfo?.users
                          ?.map((userId) =>
                            companyMemberData?.data?.decryptedData?.find(
                              (member) => member._id === userId,
                            ),
                          )
                          .filter(Boolean) || []
                      }
                      onChange={(_, newValue) => {
                        setProjectInfo({
                          ...projectInfo,
                          users: newValue.map((user) => user._id),
                        });
                      }}
                      renderTags={(tagValues, getTagProps) =>
                        tagValues?.map((option, index) => {
                          if (!option?._id || !option?.fullName) {
                            console.error("Invalid user data", option);
                            return null;
                          }
                          return (
                            <Chip
                              key={option?._id || index}
                              label={option?.fullName}
                              avatar={
                                <Avatar
                                  sx={{
                                    bgcolor: `${getUserAvatarColor(index)} !important`,
                                    width: 24,
                                    height: 24,
                                    fontSize: "0.75rem",
                                    color: "#fff !important",
                                  }}
                                >
                                  {getInitials(option?.fullName)}
                                </Avatar>
                              }
                              {...getTagProps({ index })}
                              onDelete={() => {
                                const updatedUsers = projectInfo?.users?.filter(
                                  (userId) => userId !== option._id,
                                );
                                setProjectInfo({
                                  ...projectInfo,
                                  users: updatedUsers,
                                });
                              }}
                            />
                          );
                        })
                      }
                      renderOption={(props, option, { selected }) => {
                        const optionIndex =
                          companyMemberData?.data?.decryptedData?.findIndex(
                            (item) => item._id === option._id,
                          ) || 0;
                        return (
                          <li {...props}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              <Avatar
                                sx={{
                                  backgroundColor:
                                    getUserAvatarColor(optionIndex),
                                  width: 32,
                                  height: 32,
                                  fontSize: "0.75rem",
                                  color: "#fff",
                                  mr: 1.5,
                                }}
                              >
                                {getInitials(option?.fullName)}
                              </Avatar>
                              <Typography sx={{ flexGrow: 1 }}>
                                {option?.fullName}
                              </Typography>
                              {selected && (
                                <Check
                                  sx={{
                                    color: "primary.main",
                                    ml: 1,
                                  }}
                                />
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("Add Users")}
                          placeholder={t("Select users to add to project")}
                          fullWidth
                          required={projectInfo?.users?.length === 0}
                        />
                      )}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                )}
                <Box display="flex" gap={2}>
                  {/* <Autocomplete
                    multiple
                    options={skillSetOptions}
                    value={projectInfo.skillSet || []}
                    onChange={(_, newValue) => {
                      setProjectInfo({
                        ...projectInfo,
                        skillSet: newValue,
                      });
                    }}
                    renderTags={(tagValues, getTagProps) =>
                      tagValues.map((option, index) => (
                        <Chip
                          key={index}
                          label={option}
                          {...getTagProps({ index })}
                          color="primary"
                          variant="outlined"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("Skill Set (Optional)")}
                        placeholder={t("Select skill sets")}
                        fullWidth
                      />
                    )}
                    sx={{ flex: 1 }}
                  /> */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderRadius: "8px",
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      px: 2,
                      py: 1,
                      height: "56px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, fontSize: "13px" }}
                    >
                      {t("Estimation Required")}
                    </Typography>
                    <IOSSwitch
                      checked={projectInfo.isEstimationRequired}
                      onChange={(e) => {
                        setProjectInfo({
                          ...projectInfo,
                          isEstimationRequired: e.target.checked,
                        });
                      }}
                    />
                  </Box>
                </Box>
              </Paper>}

              {/* Applications Section */}
              {!isPmoHub && !isSniffer && (
                <ApplicationSection
                  applications={projectInfo.applications || []}
                  onChange={(applications) =>
                    setProjectInfo({ ...projectInfo, applications })
                  }
                  projectId={id}
                />
              )}

              {/* Communication Channel Section - Only show when platform is SLACK or TEAMS */}
              {id && (platform === "SLACK" || platform === "TEAMS") && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 2.5,
                    borderRadius: "10px",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: (theme) => theme.palette.background.paper,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    fontSize="13px"
                    mb={2}
                    color="text.secondary"
                  >
                    {t("Communication Channel")}
                  </Typography>

                  {/* Warning when switching Communication Channel */}
                  {(() => {
                    const isCommunicationChannel = ["SLACK", "TEAMS"].includes(
                      platform,
                    );
                    const existingChannel =
                      projectDetails?.communicationChannel;
                    const showChannelWarning =
                      isCommunicationChannel &&
                      existingChannel &&
                      existingChannel !== "NONE" &&
                      existingChannel !== platform;

                    if (showChannelWarning) {
                      const existingName =
                        existingChannel === "SLACK"
                          ? "Slack"
                          : "Microsoft Teams";
                      const newName =
                        platform === "SLACK" ? "Slack" : "Microsoft Teams";

                      return (
                        <Box
                          sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: "8px",
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 152, 0, 0.1)"
                                : "#fff3e0",
                            border: (theme) =>
                              `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 152, 0, 0.3)" : "#ffcc80"}`,
                          }}
                        >
                          <Box display="flex" alignItems="flex-start" gap={1.5}>
                            <Box flex={1}>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mb={0.5}
                              >
                                <WarningAmberIcon
                                  sx={{ color: "#f57c00", fontSize: 18 }}
                                />
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  fontSize="13px"
                                  color="#e65100"
                                >
                                  {t("Switching Communication Channel")}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                fontSize="12px"
                                color="text.secondary"
                                lineHeight={1.5}
                              >
                                {t("switchingChannelWarning", {
                                  existing: existingName,
                                  new: newName,
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                    return null;
                  })()}

                  {(() => {
                    // Get the config list - handle both direct array and localData structure
                    const configList = Array.isArray(configData)
                      ? configData
                      : configData?.localData || [];

                    // Filter webhooks based on the selected platform
                    const filteredWebhooks = configList.filter(
                      (config) => config.type === platform,
                    );

                    // Find currently selected webhook - handle both string and array webhookUrl
                    const savedWebhookUrl =
                      projectInfo.communicationChannelData?.webhookUrl;
                    const selectedWebhook = filteredWebhooks.find((config) => {
                      const configUrl = Array.isArray(config.webhookUrl)
                        ? config.webhookUrl[0]
                        : config.webhookUrl;
                      const savedUrl = Array.isArray(savedWebhookUrl)
                        ? savedWebhookUrl[0]
                        : savedWebhookUrl;
                      return (
                        String(configUrl).trim() === String(savedUrl).trim()
                      );
                    });

                    const PlatformIcon =
                      platform === "SLACK" ? SlackIcon : TeamsIcon;

                    return (
                      <>
                        <Autocomplete
                          options={filteredWebhooks}
                          getOptionLabel={(option) => {
                            const channelType =
                              option.type === "SLACK"
                                ? "Slack"
                                : "Microsoft Teams";
                            const webhookUrl = String(option.webhookUrl || "");
                            const truncatedUrl =
                              webhookUrl.length > 70
                                ? webhookUrl.substring(0, 70) + "..."
                                : webhookUrl;
                            return `${option.name || channelType} - ${truncatedUrl}`;
                          }}
                          value={selectedWebhook || null}
                          onChange={(_, newValue) => {
                            if (!newValue) {
                              setProjectInfo({
                                ...projectInfo,
                                communicationChannel: "NONE",
                                communicationChannelData: null,
                              });
                            } else {
                              // Ensure webhookUrl is a string, not an array
                              const webhookUrl = Array.isArray(
                                newValue.webhookUrl,
                              )
                                ? newValue.webhookUrl[0]
                                : newValue.webhookUrl;

                              setProjectInfo({
                                ...projectInfo,
                                communicationChannel: newValue.type,
                                communicationChannelData: {
                                  webhookUrl: webhookUrl,
                                },
                              });
                            }
                          }}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  width: "100%",
                                  flexDirection: "column",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      flexGrow: 1,
                                      fontWeight: 500,
                                      fontSize: "13px",
                                    }}
                                  >
                                    {option.name ||
                                      t("{{type}} Webhook", {
                                        type: option.type,
                                      })}
                                  </Typography>
                                  {selected && (
                                    <Check
                                      sx={{
                                        color: (theme) =>
                                          theme.palette.primary.main,
                                        ml: 1,
                                      }}
                                    />
                                  )}
                                </Box>
                                <Tooltip
                                  title={option.webhookUrl || ""}
                                  placement="bottom-start"
                                  arrow
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      mt: 0.5,
                                      ml: 0,
                                      fontSize: "11px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      width: "100%",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {option.webhookUrl}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t(
                                platform === "SLACK"
                                  ? "Select Slack Webhook"
                                  : "Select Teams Webhook",
                              )}
                              placeholder={t(
                                platform === "SLACK"
                                  ? "Choose a Slack webhook"
                                  : "Choose a Teams webhook",
                              )}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <Box
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "6px",
                                        bgcolor: (theme) =>
                                          theme.palette.mode === "dark"
                                            ? "rgba(255, 255, 255, 0.06)"
                                            : "rgba(0, 0, 0, 0.04)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        ml: 0.5,
                                        mr: 1,
                                        flexShrink: 0,
                                        "& svg": {
                                          width: "18px",
                                          height: "18px",
                                        },
                                      }}
                                    >
                                      <PlatformIcon />
                                    </Box>
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />

                        {filteredWebhooks.length === 0 && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.5,
                              borderRadius: "6px",
                              bgcolor: (theme) =>
                                `${theme.palette.warning.main}08`,
                              border: (theme) =>
                                `1px solid ${theme.palette.warning.main}30`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="warning.main"
                              sx={{ fontSize: "11.5px" }}
                            >
                              {t(
                                platform === "SLACK"
                                  ? "No Slack webhooks configured. Click"
                                  : "No Teams webhooks configured. Click",
                              )}{" "}
                              <span
                                onClick={handleClickHere}
                                style={{
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  fontWeight: 600,
                                }}
                              >
                                {t("here")}
                              </span>{" "}
                              {t("to configure webhooks.")}
                            </Typography>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Paper>
              )}

              {id && projectInfo?.platform === "JIRA" && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 2.5,
                    borderRadius: "10px",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: (theme) => theme.palette.background.paper,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    fontSize="13px"
                    mb={2}
                    color="text.secondary"
                  >
                    {platform === "JIRA"
                      ? t("Jira Integration")
                      : platform === "ASANA"
                        ? t("Asana Integration")
                        : t("Integration")}
                  </Typography>

                  {/* Warning when switching Project Management integration */}
                  {(() => {
                    const isProjectManagement = [
                      "JIRA",
                      "AZURE",
                      "ASANA",
                    ].includes(platform);
                    const existingIntegration = projectDetails?.integrationType;
                    const showProjectWarning =
                      isProjectManagement &&
                      existingIntegration &&
                      existingIntegration !== "NONE" &&
                      existingIntegration !== platform;

                    if (showProjectWarning) {
                      const existingName =
                        existingIntegration === "JIRA"
                          ? "Jira"
                          : existingIntegration === "AZURE"
                            ? "Azure Boards"
                            : "Asana";
                      const newName =
                        platform === "JIRA"
                          ? "Jira"
                          : platform === "AZURE"
                            ? "Azure Boards"
                            : "Asana";

                      return (
                        <Box
                          sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: "8px",
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 152, 0, 0.1)"
                                : "#fff3e0",
                            border: (theme) =>
                              `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 152, 0, 0.3)" : "#ffcc80"}`,
                          }}
                        >
                          <Box display="flex" alignItems="flex-start" gap={1.5}>
                            <Box flex={1}>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mb={0.5}
                              >
                                <WarningAmberIcon
                                  sx={{ color: "#f57c00", fontSize: 18 }}
                                />
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  fontSize="13px"
                                  color="#e65100"
                                >
                                  {t(
                                    "Switching Project Management Integration",
                                  )}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                fontSize="12px"
                                color="text.secondary"
                                lineHeight={1.5}
                              >
                                {t("switchingIntegrationWarning", {
                                  existing: existingName,
                                  new: newName,
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                    return null;
                  })()}

                  <Autocomplete
                    options={jiraProjects?.length > 0 ? jiraProjects : []}
                    getOptionLabel={(option) => option?.name || ""}
                    value={
                      projectInfo?.jiraConfig?.length > 0 &&
                      jiraProjects?.length > 0
                        ? jiraProjects?.find(
                            (p) =>
                              p?.id === projectInfo.jiraConfig[0]?.projectId,
                          ) || null
                        : null
                    }
                    onChange={handleProjectChange}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Avatar
                            src={option?.avatarUrls["48x48"]}
                            sx={{ width: 24, height: 24, mr: 1 }}
                          />
                          <Typography sx={{ flexGrow: 1 }}>
                            {option?.name} ({option?.key})
                          </Typography>
                          {selected && (
                            <Check
                              sx={{
                                color: (theme) => theme.palette.primary.main,
                                ml: 1,
                              }}
                            />
                          )}
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t(
                          platform === "JIRA"
                            ? "Select Jira Project"
                            : platform === "ASANA"
                              ? "Select Asana Project"
                              : "Select Project",
                        )}
                        placeholder={t(
                          platform === "JIRA"
                            ? "Select Jira Project"
                            : platform === "ASANA"
                              ? "Select Asana Project"
                              : "Select Project",
                        )}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {projectInfo?.jiraConfig?.length > 0 &&
                                jiraProjects?.length > 0 &&
                                jiraProjects?.find(
                                  (p) =>
                                    p?.id ===
                                    projectInfo.jiraConfig[0]?.projectId,
                                ) && (
                                  <Avatar
                                    src={
                                      jiraProjects?.find(
                                        (p) =>
                                          p?.id ===
                                          projectInfo.jiraConfig[0]?.projectId,
                                      )?.avatarUrls?.["48x48"]
                                    }
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      mr: 1,
                                      ml: 0.5,
                                    }}
                                  />
                                )}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    disabled={!isJiraConfigured}
                  />

                  {!isJiraConfigured && (
                    <Box
                      sx={{
                        mt: 1,
                        mb: 1,
                        p: 1.5,
                        borderRadius: "6px",
                        bgcolor: (theme) => `${theme.palette.error.main}08`,
                        border: (theme) =>
                          `1px solid ${theme.palette.error.main}30`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "11.5px" }}
                      >
                        {t(
                          platform === "JIRA"
                            ? "JIRA integration is not configured or does not exist any project. Click"
                            : platform === "ASANA"
                              ? "ASANA integration is not configured or does not exist any project. Click"
                              : "Integration is not configured or does not exist any project. Click",
                        )}{" "}
                        <span
                          onClick={() => {
                            handleClickHere();
                          }}
                          style={{
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 600,
                          }}
                        >
                          {t("here")}
                        </span>{" "}
                        {t("to integrate.")}
                      </Typography>
                    </Box>
                  )}

                  {/* JIRA Configuration Tabs */}
                  {projectInfo?.jiraConfig?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <TabContext value={tabValue.toString()}>
                        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                          <TabList
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                              minHeight: 36,
                              "& .MuiTab-root": {
                                minHeight: 36,
                                fontSize: "12.5px",
                                textTransform: "none",
                                fontWeight: 500,
                                py: 0.5,
                              },
                            }}
                          >
                            {projectInfo?.jiraConfig?.map((config, index) => (
                              <Tab
                                key={config.key}
                                label={config.projectData?.name || config.key}
                                value={(index + 1).toString()}
                              />
                            ))}
                          </TabList>
                        </Box>

                        {projectInfo?.jiraConfig?.map((config, index) => {
                          const currentProject = combinedResponse?.find(
                            (item) => item.key === config.key,
                          );
                          const currentStatus = combinedStatus?.find(
                            (item) => item.key === config.key,
                          );

                          return (
                            <TabPanel
                              key={config.key}
                              value={(index + 1).toString()}
                              sx={{ px: 0.5, py: 1.5 }}
                            >
                              {/* Sync Configuration Section */}
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 2,
                                  mb: 2,
                                  borderRadius: "8px",
                                  border: (theme) =>
                                    `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={600}
                                  fontSize="12.5px"
                                  mb={1.5}
                                  color="text.secondary"
                                >
                                  {t("Sync Configuration")}
                                </Typography>

                                <Stack spacing={1.5}>
                                  {/* Sync Tasks to JIRA */}
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box>
                                      <Typography variant="body2">
                                        {t("Automatically create task in JIRA")}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {t(
                                          "When a task is created, it will be automatically synced to JIRA",
                                        )}
                                      </Typography>
                                    </Box>
                                    <IOSSwitch
                                      checked={
                                        config.fieldConfig
                                          ?.isAutomaticCreateJira || false
                                      }
                                      onChange={(e) =>
                                        updateFieldConfig(
                                          config.key,
                                          "isAutomaticCreateJira",
                                          e.target.checked,
                                          "boolean",
                                        )
                                      }
                                    />
                                  </Box>

                                  {/* Sync Comments to JIRA */}
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box>
                                      <Typography variant="body2">
                                        {t("Sync comments to JIRA")}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {t(
                                          "Comments added here will be synced to JIRA",
                                        )}
                                      </Typography>
                                    </Box>
                                    <IOSSwitch
                                      checked={
                                        config.fieldConfig?.isCreateComment ||
                                        false
                                      }
                                      onChange={(e) =>
                                        updateFieldConfig(
                                          config.key,
                                          "isCreateComment",
                                          e.target.checked,
                                          "boolean",
                                        )
                                      }
                                    />
                                  </Box>

                                  {/* Sync Comments from JIRA */}
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box>
                                      <Typography variant="body2">
                                        {t("Sync comments from JIRA")}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {t(
                                          "Comments from JIRA will be synced here",
                                        )}
                                      </Typography>
                                    </Box>
                                    <IOSSwitch
                                      checked={
                                        config.fieldConfig?.isSyncComment ||
                                        false
                                      }
                                      onChange={(e) =>
                                        updateFieldConfig(
                                          config.key,
                                          "isSyncComment",
                                          e.target.checked,
                                          "boolean",
                                        )
                                      }
                                    />
                                  </Box>

                                  {/* Sync from JIRA */}
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box>
                                      <Typography variant="body2">
                                        {t("Sync tasks from JIRA")}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {t(
                                          "Tasks created in JIRA will be synced here",
                                        )}
                                      </Typography>
                                    </Box>
                                    <IOSSwitch
                                      checked={
                                        config.fieldConfig?.syncFromJira ||
                                        false
                                      }
                                      onChange={(e) =>
                                        updateFieldConfig(
                                          config.key,
                                          "syncFromJira",
                                          e.target.checked,
                                          "boolean",
                                        )
                                      }
                                    />
                                  </Box>
                                </Stack>
                              </Paper>

                              {/* Status Mapping Section */}
                              {currentStatus?.status?.length > 0 && (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: "8px",
                                    border: (theme) =>
                                      `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={600}
                                    fontSize="12.5px"
                                    mb={1.5}
                                    color="text.secondary"
                                  >
                                    {t("Status Mapping")}
                                  </Typography>

                                  <Box display="flex" gap={2}>
                                    <FormControl fullWidth>
                                      <InputLabel>
                                        {t("Completed Status (JIRA)")}
                                      </InputLabel>
                                      <Select
                                        value={
                                          config.fieldConfig?.completedStatus ||
                                          ""
                                        }
                                        onChange={(e) =>
                                          updateFieldConfig(
                                            config.key,
                                            "completedStatus",
                                            e.target.value,
                                          )
                                        }
                                        label={t("Completed Status (JIRA)")}
                                      >
                                        {currentStatus.status.map((status) => (
                                          <MenuItem
                                            key={status.id}
                                            value={status.id}
                                          >
                                            {status.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>

                                    <FormControl fullWidth>
                                      <InputLabel>
                                        {t("Completed Column (Local)")}
                                      </InputLabel>
                                      <Select
                                        value={
                                          config.fieldConfig?.completedColumn ||
                                          ""
                                        }
                                        onChange={(e) =>
                                          updateFieldConfig(
                                            config.key,
                                            "completedColumn",
                                            e.target.value,
                                          )
                                        }
                                        label={t("Completed Column (Local)")}
                                      >
                                        {statusList?.data?.decryptedData?.items?.map(
                                          (status) => (
                                            <MenuItem
                                              key={status._id}
                                              value={status._id}
                                            >
                                              {status.statusName}
                                            </MenuItem>
                                          ),
                                        ) ||
                                          defaultStatus?.data?.decryptedData?.items?.map(
                                            (status) => (
                                              <MenuItem
                                                key={status._id}
                                                value={status._id}
                                              >
                                                {status.statusName}
                                              </MenuItem>
                                            ),
                                          )}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Paper>
                              )}

                              {/* Required Fields Section */}
                              {currentProject?.data?.length > 0 && (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    borderRadius: "8px",
                                    border: (theme) =>
                                      `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={600}
                                    fontSize="12.5px"
                                    mb={1.5}
                                    color="text.secondary"
                                  >
                                    {t("Required Fields Configuration")}
                                  </Typography>

                                  <TableContainer
                                    sx={{
                                      borderRadius: "6px",
                                      border: (theme) =>
                                        `1px solid ${theme.palette.divider}`,
                                    }}
                                  >
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>
                                            <strong>{t("Field Name")}</strong>
                                          </TableCell>
                                          <TableCell>
                                            <strong>
                                              {t("Default Value")}
                                            </strong>
                                          </TableCell>
                                          <TableCell align="center">
                                            <strong>{t("Auto-fill")}</strong>
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {currentProject.data.map((field) => (
                                          <TableRow key={field.fieldKey}>
                                            <TableCell>
                                              {field.fieldName}
                                            </TableCell>
                                            <TableCell>
                                              {field.dataType === "array" ? (
                                                <Select
                                                  size="small"
                                                  fullWidth
                                                  value={
                                                    config.defaultFields?.[
                                                      field.fieldName
                                                    ]?.[field.fieldKey] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleChange(
                                                      config.key,
                                                      field.fieldName,
                                                      field.fieldKey,
                                                      e.target.value,
                                                      field.dataType,
                                                      field.allowedValues,
                                                    )
                                                  }
                                                >
                                                  {field.allowedValues?.map(
                                                    (val) => (
                                                      <MenuItem
                                                        key={val.id}
                                                        value={val.id}
                                                      >
                                                        {val.name || val.value}
                                                      </MenuItem>
                                                    ),
                                                  )}
                                                </Select>
                                              ) : (
                                                <TextField
                                                  size="small"
                                                  fullWidth
                                                  type={
                                                    field.dataType === "number"
                                                      ? "number"
                                                      : "text"
                                                  }
                                                  value={
                                                    config.defaultFields?.[
                                                      field.fieldName
                                                    ]?.[field.fieldKey] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleChange(
                                                      config.key,
                                                      field.fieldName,
                                                      field.fieldKey,
                                                      e.target.value,
                                                      field.dataType,
                                                    )
                                                  }
                                                />
                                              )}
                                            </TableCell>
                                            <TableCell align="center">
                                              <Switch
                                                checked={
                                                  config.defaultFields?.[
                                                    field.fieldName
                                                  ]?.method || false
                                                }
                                                onChange={(e) =>
                                                  handleChange(
                                                    config.key,
                                                    field.fieldName,
                                                    field.fieldKey,
                                                    e.target.checked,
                                                    field.dataType,
                                                    field.allowedValues,
                                                  )
                                                }
                                                size="small"
                                              />
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Paper>
                              )}

                              {loading && (
                                <Box
                                  display="flex"
                                  justifyContent="center"
                                  mt={2}
                                >
                                  <CircularProgress size={24} />
                                </Box>
                              )}
                            </TabPanel>
                          );
                        })}
                      </TabContext>
                    </Box>
                  )}
                </Paper>
              )}

              {id && platform === "AZURE" && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 2.5,
                    borderRadius: "10px",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: (theme) => theme.palette.background.paper,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    fontSize="13px"
                    mb={2}
                    color="text.secondary"
                  >
                    {t("Azure Integration")}
                  </Typography>

                  {/* Warning when switching Project Management integration */}
                  {(() => {
                    const isProjectManagement = [
                      "JIRA",
                      "AZURE",
                      "ASANA",
                    ].includes(platform);
                    const existingIntegration = projectDetails?.integrationType;
                    const showProjectWarning =
                      isProjectManagement &&
                      existingIntegration &&
                      existingIntegration !== "NONE" &&
                      existingIntegration !== platform;

                    if (showProjectWarning) {
                      const existingName =
                        existingIntegration === "JIRA"
                          ? "Jira"
                          : existingIntegration === "AZURE"
                            ? "Azure Boards"
                            : "Asana";
                      const newName =
                        // @ts-ignore
                        platform === "JIRA"
                          ? "Jira"
                          : platform === "AZURE"
                            ? "Azure Boards"
                            : "Asana";

                      return (
                        <Box
                          sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: "8px",
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 152, 0, 0.1)"
                                : "#fff3e0",
                            border: (theme) =>
                              `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 152, 0, 0.3)" : "#ffcc80"}`,
                          }}
                        >
                          <Box display="flex" alignItems="flex-start" gap={1.5}>
                            <Box flex={1}>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mb={0.5}
                              >
                                <WarningAmberIcon
                                  sx={{ color: "#f57c00", fontSize: 18 }}
                                />
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  fontSize="13px"
                                  color="#e65100"
                                >
                                  {t(
                                    "Switching Project Management Integration",
                                  )}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                fontSize="12px"
                                color="text.secondary"
                                lineHeight={1.5}
                              >
                                {t("switchingIntegrationWarning", {
                                  existing: existingName,
                                  new: newName,
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                    return null;
                  })()}

                  <Box display="flex" gap={2} mb={2}>
                    <TextField
                      label={t("Azure Organization")}
                      fullWidth
                      variant="outlined"
                      value={
                        projectInfo.azureBoardsConfig?.[0]?.organization || ""
                      }
                      required
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setProjectInfo((prev) => {
                          const updated = [...(prev.azureBoardsConfig || [])];
                          if (updated.length === 0) {
                            updated.push({
                              organization: "",
                              projectName: "",
                              projectId: "",
                              projectData: {},
                              fieldConfig: { ...defaultAzureFieldConfig },
                              syncFromAzure: false,
                              syncToAzure: false,
                              syncCommentFromAzure: false,
                              syncCommentToAzure: false,
                              defaultFields: {},
                            });
                          }
                          updated[0] = {
                            ...updated[0],
                            organization: e.target.value,
                          };
                          return {
                            ...prev,
                            azureBoardsConfig: updated,
                          };
                        });
                      }}
                      disabled={!isAzureConfigured}
                    />
                  </Box>

                  <Autocomplete
                    options={Array.isArray(azureProjects) ? azureProjects : []}
                    getOptionLabel={(option) => option?.name || ""}
                    loading={isLoadingAzureProjects}
                    value={
                      projectInfo.azureBoardsConfig?.length > 0 &&
                      Array.isArray(azureProjects) &&
                      azureProjects.length > 0
                        ? azureProjects.find(
                            (p) =>
                              p?.id ===
                              projectInfo.azureBoardsConfig[0]?.projectId,
                          ) || null
                        : null
                    }
                    onChange={handleAzureProjectChange}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>
                            {option?.name}
                          </Typography>
                          {selected && (
                            <Check
                              sx={{
                                color: (theme) => theme.palette.primary.main,
                                ml: 1,
                              }}
                            />
                          )}
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("Select Azure Project")}
                        placeholder={t("Select Azure Project")}
                        required
                      />
                    )}
                    disabled={!isAzureConfigured}
                  />

                  {!isAzureConfigured && (
                    <Box
                      sx={{
                        mt: 1,
                        mb: 1,
                        p: 1.5,
                        borderRadius: "6px",
                        bgcolor: (theme) => `${theme.palette.error.main}08`,
                        border: (theme) =>
                          `1px solid ${theme.palette.error.main}30`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "11.5px" }}
                      >
                        {t("Azure Boards integration is not configured. Click")}{" "}
                        <span
                          onClick={() => {
                            handleClickHere();
                          }}
                          style={{
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 600,
                          }}
                        >
                          {t("here")}
                        </span>{" "}
                        {t("to integrate.")}
                      </Typography>
                    </Box>
                  )}

                  {/* Azure Configuration Tabs */}
                  {projectInfo?.azureBoardsConfig?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <TabContext value={azureTabValue.toString()}>
                        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                          <TabList
                            onChange={(_, newValue) =>
                              setAzureTabValue(newValue)
                            }
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                              minHeight: 36,
                              "& .MuiTab-root": {
                                minHeight: 36,
                                fontSize: "12.5px",
                                textTransform: "none",
                                fontWeight: 500,
                                py: 0.5,
                              },
                            }}
                          >
                            {projectInfo?.azureBoardsConfig?.map(
                              (config, index) => (
                                <Tab
                                  key={config.projectId}
                                  label={
                                    config.projectData?.name ||
                                    config.projectName
                                  }
                                  value={(index + 1).toString()}
                                />
                              ),
                            )}
                          </TabList>
                        </Box>

                        {projectInfo?.azureBoardsConfig?.map(
                          (config, index) => (
                            <TabPanel
                              key={config.projectId}
                              value={(index + 1).toString()}
                              sx={{ px: 0.5, py: 1.5 }}
                            >
                              {/* Sync Configuration Section */}
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 2,
                                  mb: 2,
                                  borderRadius: "8px",
                                  border: (theme) =>
                                    `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={600}
                                  fontSize="12.5px"
                                  mb={1.5}
                                  color="text.secondary"
                                >
                                  {t("Sync Configuration")}
                                </Typography>

                                <Stack spacing={1.5}>
                                  {/* Sync Tasks to Azure */}
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box>
                                      <Typography variant="body2">
                                        {t(
                                          "Automatically create work item in Azure",
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {t(
                                          "When a task is created, it will be automatically synced to Azure Boards",
                                        )}
                                      </Typography>
                                    </Box>
                                    <IOSSwitch
                                      checked={
                                        config.fieldConfig
                                          ?.isAutomaticCreateAzure || false
                                      }
                                      onChange={(e) =>
                                        updateAzureFieldConfig(
                                          config.projectId,
                                          "isAutomaticCreateAzure",
                                          e.target.checked,
                                          "boolean",
                                        )
                                      }
                                    />
                                  </Box>

                                  {/* Sync Comments to Azure */}
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box>
                                      <Typography variant="body2">
                                        {t("Sync comments to Azure")}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {t(
                                          "Comments added here will be synced to Azure Boards",
                                        )}
                                      </Typography>
                                    </Box>
                                    <IOSSwitch
                                      checked={
                                        config.fieldConfig?.isCreateComment ||
                                        false
                                      }
                                      onChange={(e) =>
                                        updateAzureFieldConfig(
                                          config.projectId,
                                          "isCreateComment",
                                          e.target.checked,
                                          "boolean",
                                        )
                                      }
                                    />
                                  </Box>

                                  {/* Sync Comments from Azure */}
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box>
                                      <Typography variant="body2">
                                        {t("Sync comments from Azure")}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {t(
                                          "Comments from Azure Boards will be synced here",
                                        )}
                                      </Typography>
                                    </Box>
                                    <IOSSwitch
                                      checked={
                                        config.fieldConfig?.isSyncComment ||
                                        false
                                      }
                                      onChange={(e) =>
                                        updateAzureFieldConfig(
                                          config.projectId,
                                          "isSyncComment",
                                          e.target.checked,
                                          "boolean",
                                        )
                                      }
                                    />
                                  </Box>

                                  {/* Sync from Azure */}
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box>
                                      <Typography variant="body2">
                                        {t("Sync work items from Azure")}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {t(
                                          "Work items created in Azure Boards will be synced here",
                                        )}
                                      </Typography>
                                    </Box>
                                    <IOSSwitch
                                      checked={
                                        config.fieldConfig?.syncFromAzure ||
                                        false
                                      }
                                      onChange={(e) =>
                                        updateAzureFieldConfig(
                                          config.projectId,
                                          "syncFromAzure",
                                          e.target.checked,
                                          "boolean",
                                        )
                                      }
                                    />
                                  </Box>
                                </Stack>
                              </Paper>
                            </TabPanel>
                          ),
                        )}
                      </TabContext>
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flex={1}
              p={4}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </form>
    </Drawer>
  );
}
